// Covenant Calibration (capture-only) — card 2 of 3 on the loan Profile
// tab. Designation + measurement window dropdowns, a typed reserve-months
// integer (NO calculation), and optional notes. Staff edit + confirm.

import { useEffect, useState } from "react";
import {
  Button,
  Card,
  FormField,
  Input,
  SkeletonText,
  Textarea,
} from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import {
  useConfirmSeasonality,
  useCovenantCalibration,
  useUpdateSeasonality,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import {
  COVENANT_DESIGNATION_LABEL,
  COVENANT_MEASUREMENT_WINDOW_LABEL,
  type CovenantDesignation,
  type CovenantMeasurementWindow,
  type CovenantSeasonalityDto,
  type UpdateSeasonalityRequest,
} from "@/api/portfolioAdmin/types";
import { CovenantConfirmRow } from "./CovenantConfirmRow";

const DESIGNATIONS = Object.keys(
  COVENANT_DESIGNATION_LABEL,
) as CovenantDesignation[];
const WINDOWS = Object.keys(
  COVENANT_MEASUREMENT_WINDOW_LABEL,
) as CovenantMeasurementWindow[];

interface Draft {
  designation: CovenantDesignation;
  measurementWindow: CovenantMeasurementWindow | "";
  reserveMonths: string;
  notes: string;
}

function hydrate(s: CovenantSeasonalityDto): Draft {
  return {
    designation: s.designation,
    measurementWindow: s.measurementWindow ?? "",
    reserveMonths: s.reserveMonths != null ? String(s.reserveMonths) : "",
    notes: s.notes ?? "",
  };
}

export function SeasonalityReserveCard({ loanId }: { loanId: string }) {
  const auth = useAuth();
  const canEdit = auth.hasPermission("Loans.Manage") || auth.hasRoleCode("ADMIN");
  const toast = useToast();
  const query = useCovenantCalibration(loanId);
  const update = useUpdateSeasonality(loanId);
  const confirm = useConfirmSeasonality(loanId);

  const data = query.data;
  const seasonality = data?.seasonality;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Re-hydrate when an edit starts (or fresh data arrives mid-edit).
  useEffect(() => {
    if (editing && seasonality) setDraft(hydrate(seasonality));
  }, [editing, seasonality]);

  const startEdit = () => {
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    if (!draft) return;
    setError(null);
    let reserveMonths: number | null = null;
    if (draft.reserveMonths.trim() !== "") {
      const n = Number(draft.reserveMonths);
      if (!Number.isInteger(n) || n < 0) {
        setError("Reserve months must be a whole number of 0 or more.");
        return;
      }
      reserveMonths = n;
    }
    const body: UpdateSeasonalityRequest = {
      designation: draft.designation,
      measurementWindow: draft.measurementWindow === "" ? null : draft.measurementWindow,
      reserveMonths,
      notes: draft.notes.trim() === "" ? null : draft.notes.trim(),
    };
    try {
      await update.mutateAsync(body);
      toast.success("Seasonality & reserve saved");
      setEditing(false);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const onConfirm = async () => {
    try {
      await confirm.mutateAsync();
      toast.success("Seasonality & reserve confirmed");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Card data-testid="covenant-seasonality-card">
      <div className="mb-space-3 flex items-start justify-between gap-space-3">
        <div>
          <div className="font-display font-bold text-h3 text-onyx">
            Seasonality &amp; Reserve
          </div>
          <p className="text-caption text-gray-mid m-0">
            Measurement designation + reserve — capture only.
          </p>
        </div>
        {canEdit && !editing && !query.isLoading && (
          <Button
            variant="tertiary"
            size="sm"
            onClick={startEdit}
            data-testid="covenant-seasonality-edit"
          >
            Edit
          </Button>
        )}
      </div>

      {query.isLoading ? (
        <SkeletonText lines={4} />
      ) : query.isError || !seasonality || !data ? (
        <p className="text-small text-status-error m-0" role="alert">
          Couldn&apos;t load seasonality &amp; reserve. Please retry.
        </p>
      ) : !editing ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 text-small">
            <Value label="Designation">
              {COVENANT_DESIGNATION_LABEL[seasonality.designation]}
            </Value>
            <Value label="Measurement window">
              {seasonality.measurementWindow
                ? COVENANT_MEASUREMENT_WINDOW_LABEL[seasonality.measurementWindow]
                : "—"}
            </Value>
            <Value label="Reserve months">
              {seasonality.reserveMonths != null ? String(seasonality.reserveMonths) : "—"}
            </Value>
            <Value label="Notes" wide>
              <span className="whitespace-pre-wrap">{seasonality.notes || "—"}</span>
            </Value>
          </div>
          <CovenantConfirmRow
            confirmation={data.seasonalityConfirmation}
            canEdit={canEdit}
            editing={editing}
            onConfirm={() => void onConfirm()}
            confirming={confirm.isPending}
            testIdPrefix="covenant-seasonality"
          />
        </>
      ) : (
        draft && (
          <div data-testid="covenant-seasonality-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
              <FormField label="Designation" htmlFor="seasonality-designation">
                <Select
                  id="seasonality-designation"
                  aria-label="Designation"
                  value={draft.designation}
                  onChange={(e) =>
                    setDraft((d) =>
                      d
                        ? { ...d, designation: e.target.value as CovenantDesignation }
                        : d,
                    )
                  }
                >
                  {DESIGNATIONS.map((v) => (
                    <option key={v} value={v}>
                      {COVENANT_DESIGNATION_LABEL[v]}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Measurement window" htmlFor="seasonality-window">
                <Select
                  id="seasonality-window"
                  aria-label="Measurement window"
                  value={draft.measurementWindow}
                  onChange={(e) =>
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            measurementWindow: e.target
                              .value as CovenantMeasurementWindow | "",
                          }
                        : d,
                    )
                  }
                >
                  <option value="">—</option>
                  {WINDOWS.map((v) => (
                    <option key={v} value={v}>
                      {COVENANT_MEASUREMENT_WINDOW_LABEL[v]}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Reserve months" htmlFor="seasonality-reserve-months">
                <Input
                  id="seasonality-reserve-months"
                  type="number"
                  min={0}
                  step={1}
                  aria-label="Reserve months"
                  value={draft.reserveMonths}
                  onChange={(e) =>
                    setDraft((d) => (d ? { ...d, reserveMonths: e.target.value } : d))
                  }
                  data-testid="covenant-seasonality-reserve-months"
                />
              </FormField>
              <FormField label="Notes" htmlFor="seasonality-notes" className="md:col-span-2">
                <Textarea
                  id="seasonality-notes"
                  aria-label="Notes"
                  rows={3}
                  value={draft.notes}
                  onChange={(e) =>
                    setDraft((d) => (d ? { ...d, notes: e.target.value } : d))
                  }
                />
              </FormField>
            </div>

            {error && (
              <p
                className="text-caption text-status-error mt-space-3 m-0"
                role="alert"
                data-testid="covenant-seasonality-error"
              >
                {error}
              </p>
            )}

            <div className="mt-space-3 flex justify-end gap-space-2">
              <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void save()}
                loading={update.isPending}
                data-testid="covenant-seasonality-save"
              >
                Save
              </Button>
            </div>
          </div>
        )
      )}
    </Card>
  );
}

function Value({
  label,
  children,
  wide,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={wide ? "md:col-span-2" : undefined}>
      <div className="font-display text-caption uppercase tracking-label text-gray-mid">
        {label}
      </div>
      <div className="text-onyx mt-[2px]">{children}</div>
    </div>
  );
}
